import { NavLink, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Globe2, MapPinned, FolderKanban, Users, ClipboardList,
  FileBarChart2, Settings2, ScrollText, LogOut, ShieldCheck, Plug, Wrench,
  CalendarDays, Map, Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import TopBar from "@/components/TopBar";

const NAV = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true, key: "dashboard" },
  { to: "/employee", icon: Clock, label: "My attendance", key: "my_attendance", centerAdminOnly: true },
  { to: "/admin/attendance", icon: ClipboardList, label: "Attendance", key: "attendance" },
  { to: "/admin/attendance-matrix", icon: CalendarDays, label: "Monthly View", key: "monthly_view" },
  { to: "/admin/tracking", icon: Map, label: "Location Map", key: "location_map" },
  { to: "/admin/corrections", icon: Wrench, label: "Corrections", key: "corrections" },
  { to: "/admin/reports", icon: FileBarChart2, label: "Reports", key: "reports" },
  { to: "/admin/employees", icon: Users, label: "Employees", key: "employees", superOnly: false },
  { to: "/admin/centers", icon: MapPinned, label: "Centers", key: "centers", superOnly: true },
  { to: "/admin/countries", icon: Globe2, label: "Countries", key: "countries", superOnly: true },
  { to: "/admin/projects", icon: FolderKanban, label: "Projects", key: "projects", superOnly: true },
  { to: "/admin/policies", icon: Settings2, label: "Policies", key: "policies", superOnly: true },
  { to: "/admin/integrations", icon: Plug, label: "Integrations", key: "integrations", superOnly: true },
  { to: "/admin/audit", icon: ScrollText, label: "Audit Log", key: "audit" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const isSuper = user?.role === "super_admin";
  const isCenterAdmin = user?.role === "center_admin";
  // Center admins get filtered by their `visible_menus` list; empty/null means
  // show everything applicable to a center admin (backwards-compatible default).
  const visibleMenus = Array.isArray(user?.visible_menus) ? user.visible_menus : null;
  const canSee = (n) => {
    if (n.superOnly && !isSuper) return false;
    if (n.centerAdminOnly && !isCenterAdmin) return false;
    if (isCenterAdmin && visibleMenus && visibleMenus.length > 0) {
      // "my_attendance" and "dashboard" are always visible to center admins
      if (n.key === "my_attendance" || n.key === "dashboard") return true;
      return visibleMenus.includes(n.key);
    }
    return true;
  };

  return (
    <div className="min-h-screen flex">
      {/* Sidebar */}
      <aside data-testid="admin-sidebar" className="w-56 shrink-0 border-r border-border bg-card/40 flex flex-col">
        <div className="px-5 py-5 border-b border-border">
          <div className="font-display text-lg leading-tight tracking-tight">BT</div>
          <div className="label-mono">Control Room</div>
        </div>
        <nav className="flex-1 overflow-auto py-3">
          {NAV.filter(canSee).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-5 py-2.5 text-sm border-l-2 transition-colors ${
                  isActive
                    ? "border-l-[hsl(var(--accent))] bg-secondary text-foreground"
                    : "border-l-transparent text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                }`
              }
            >
              <n.icon className="h-4 w-4" strokeWidth={1.5} />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-border p-4 space-y-2">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-emerald-400" strokeWidth={1.5} />
            <div className="text-sm">
              <div data-testid="admin-current-name" className="font-medium leading-tight">{user?.name}</div>
              <div className="label-mono">{user?.role?.replace("_", " ")}</div>
            </div>
          </div>
          <Button
            data-testid="admin-logout-btn"
            variant="outline"
            size="sm"
            className="w-full rounded-none border-border"
            onClick={logout}
          >
            <LogOut className="h-3.5 w-3.5 mr-2" strokeWidth={1.5} /> Sign out
          </Button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <TopBar variant="admin" />
        <main className="flex-1 min-w-0 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
